import { FillerData } from "@/models/pdfModels";
import {
  centeredText,
  getTableColumFunctions,
  offset,
  otherTeam,
  trim,
} from "./utils";
import { jsPDF } from "jspdf";
import coordinatesMap from "./coordinatesMap";
import { millisToDate, millisToTime } from "@/services/dateTimeService";
import { formatScore } from "@context/utils";
import { Colors } from "./colors";
import {
  challengeComplete,
  getChallengeTypeTranslation,
  remainingChallenges,
} from "@/utils/challenge-utils";

export const fillPdf = (doc: jsPDF, data: FillerData["data"]): jsPDF => {
  const withDataAndOffset = dataAndOffsetGenerator(doc, data);

  fillTeamBlock(withDataAndOffset(...coordinatesMap["team2lock"]));
  fillMetaInfoBlock(withDataAndOffset(...coordinatesMap["metaInfoBlock"]));
  fillChallengeTable(withDataAndOffset(...coordinatesMap["challengeTable"]));
  fillRemainingChallengesBlock(
    withDataAndOffset(...coordinatesMap["remainingChallengesBlock"]),
  );

  doc.setDocumentProperties({
    title: `challenge-protocol-${[data.gameData.gameNumber, data.gameData.team1.shortName, data.gameData.team2.shortName, data.samsId].filter(Boolean).join("-")}`,
    creator: `VBL Challenge Protokoll`,
  });

  return doc;
};

const fillTeamBlock = ({ doc, offset: { x, y }, data }: FillerData) => {
  doc.setFontSize(11);
  doc.setFont("Arial", "normal");

  const { team1, team2 } = data.gameData;

  centeredText(
    doc,
    trim(team1.clubCode || team1.shortName, 5),
    x(8),
    y(13),
    16,
  );
  centeredText(doc, trim(team1.longName, 34), x(52.5), y(13), 69);
  centeredText(
    doc,
    trim(team2.clubCode || team1.shortName, 5),
    x(95),
    y(13),
    16,
  );
  centeredText(doc, trim(team2.longName, 34), x(137.5), y(13), 69);
};

const fillMetaInfoBlock = ({ doc, offset: { x, y }, data }: FillerData) => {
  doc.setFontSize(11);
  doc.setFont("Arial", "normal");

  const { gameStartDateTime, location, gameNumber } = data.gameData;

  const date = millisToDate(gameStartDateTime);
  const time = `${millisToTime(gameStartDateTime)} Uhr`;

  centeredText(doc, gameNumber, x(13), y(13), 26);
  centeredText(doc, `${date} - ${time}`, x(51), y(13), 50);
  centeredText(doc, trim(location, 52), x(123), y(13), 98);
};

const fillChallengeTable = ({ doc, offset: { x, y }, data }: FillerData) => {
  doc.setFontSize(11);
  doc.setFont("Arial", "normal");
  doc.setDrawColor(Colors.BLACK);

  const { columnWidth, columnStart, columnCenter, columnEnd } =
    getTableColumFunctions();

  centeredText(
    doc,
    data.gameData.team1.clubCode || trim(data.gameData.team1.shortName, 5),
    x(columnCenter(1)),
    y(19.5),
    columnWidth[1],
  );
  centeredText(
    doc,
    data.gameData.team2.clubCode || trim(data.gameData.team2.shortName, 5),
    x(columnCenter(2)),
    y(19.5),
    columnWidth[2],
  );

  const challenges = data.challenges.filter(
    (challenge) =>
      !challenge.id.startsWith("deleted") && challengeComplete(challenge),
  );

  for (let i = 0; i < challenges.length; i++) {
    const challenge = challenges[i];
    const yCoordinate = y(27.5 + i * 8);

    const values = [
      i,
      challenge.team === "team1" ? "X" : "",
      challenge.team === "team2" ? "X" : "",
      challenge.set,
      formatScore(challenge.scoreRallyStart, challenge.team),
      challenge.scoreRallyEnd
        ? formatScore(challenge.scoreRallyEnd, challenge.team)
        : "-",
      getChallengeTypeTranslation(challenge.typeOfChallenge, true),
      challenge.challengeResult === challenge.team ? "X" : "",
      challenge.challengeResult === otherTeam(challenge.team) ? "X" : "",
      challenge.challengeResult === "unresolvable" ? "X" : "",
      formatScore(challenge.scoreAfterChallenge, challenge.team),
    ];

    for (let c = 1; c < values.length; c++) {
      if (values[c] || values[c] === 0) {
        centeredText(
          doc,
          `${values[c]}`,
          x(columnCenter(c)),
          yCoordinate,
          columnWidth[c],
        );
      }
    }

    if (challenge.team === "ref") {
      doc.setDrawColor(Colors.WHITE);
      doc.setLineWidth(2);

      doc.line(
        x(columnEnd(2)),
        y(22.2 + i * 8),
        x(columnEnd(2)),
        y(29.8 + i * 8),
      );
      doc.line(
        x(columnEnd(8)),
        y(22.2 + i * 8),
        x(columnEnd(8)),
        y(29.8 + i * 8),
      );

      doc.setDrawColor(Colors.BLACK);
      doc.setLineWidth(1);
      centeredText(
        doc,
        `Referee`,
        x((columnCenter(1) + columnCenter(2)) / 2),
        yCoordinate,
        columnWidth[1] + columnWidth[2],
      );
      if (challenge.challengeResult !== "unresolvable") {
        centeredText(
          doc,
          data?.gameData[challenge?.challengeResult]?.shortName,
          x((columnCenter(7) + columnCenter(8)) / 2),
          yCoordinate,
          columnWidth[7] + columnWidth[8],
        );
      }
    }
  }
};

const fillRemainingChallengesBlock = ({
  doc,
  offset: { x, y },
  data,
}: FillerData) => {
  doc.setFontSize(11);
  doc.setFont("Arial", "normal");

  centeredText(
    doc,
    data.gameData.team1.clubCode || trim(data.gameData.team1.shortName, 5),
    x(17.5),
    y(10),
    14,
  );
  centeredText(
    doc,
    data.gameData.team2.clubCode || trim(data.gameData.team2.shortName, 5),
    x(32.5),
    y(10),
    14,
  );

  const remainingChallengesPerSet = Object.entries(remainingChallenges(data));

  for (let i = 1; i <= remainingChallengesPerSet.length; i++) {
    const { team1, team2 } = remainingChallengesPerSet[i - 1][1];

    if (team1 < 2) {
      doc.circle(x(14.5), y(6.5 + i * 6) + 2.5, 1, "FD");
    }
    if (team1 < 1) {
      doc.circle(x(20.5), y(6.5 + i * 6) + 2.5, 1, "FD");
    }
    if (team2 < 2) {
      doc.circle(x(29.5), y(6.5 + i * 6) + 2.5, 1, "FD");
    }
    if (team2 < 1) {
      doc.circle(x(35.5), y(6.5 + i * 6) + 2.5, 1, "FD");
    }
  }
};

const dataAndOffsetGenerator =
  (doc: jsPDF, data: FillerData["data"]) =>
  (xOffset: number, yOffset: number): FillerData => {
    return { doc, offset: offset(xOffset, yOffset), data };
  };
